import type { OrderNode, OrderLineItem } from '../graphql/queries/orders';
import {
  formatOrderTotal,
  getCustomerName,
  formatBookingDate,
  formatStatusLabel,
  normalizeOrderStatus,
  resolvePaymentStatus,
  type BookingServiceType,
  type PaymentStatusValue,
} from './bookingOrderHelpers';
import { detectOrderType, decodeHtmlEntities, metaArrayToRecord } from './orderLineItemMeta';
import { buildInvoiceNumberMap } from './invoiceNumber';

export type DashboardPeriod = 'ALL' | '1M' | '6M' | '1Y';

export interface StatusBucketMetrics {
  count: number;
  amount: number;
}

export interface ServicePerformanceMetrics {
  type: BookingServiceType;
  label: string;
  count: number;
  amount: number;
}

export interface InvoiceBucketMetrics {
  count: number;
  amount: number;
}

export interface LocationStat {
  name: string;
  count: number;
  percent: number;
}

export interface RecentActivityRow {
  date: string;
  customer: string;
  type: string;
  reference: string;
  amount: string;
  status: string;
  statusKey: string;
  orderId: number;
}

export interface DashboardInsight {
  id: string;
  icon: string;
  title: string;
  description: string;
  to: string;
}

export interface DashboardKpis {
  totalRevenue: number;
  totalBookings: number;
  totalOrders: number;
  outstandingAmount: number;
  outstandingCount: number;
  revenueSeries: number[];
  bookingsSeries: number[];
  ordersSeries: number[];
  outstandingSeries: number[];
  bookingsConfirmed: number;
  bookingsPending: number;
  ordersConfirmed: number;
  ordersPending: number;
}

export interface DashboardMetrics {
  currency?: string;
  period: DashboardPeriod;
  kpis: DashboardKpis;
  bookingStatus: {
    confirmed: StatusBucketMetrics;
    pending: StatusBucketMetrics;
    cancelled: StatusBucketMetrics;
  };
  orderStatus: {
    confirmed: StatusBucketMetrics;
    pending: StatusBucketMetrics;
    cancelled: StatusBucketMetrics;
  };
  services: ServicePerformanceMetrics[];
  invoices: {
    paid: InvoiceBucketMetrics;
    unpaid: InvoiceBucketMetrics;
    overdue: InvoiceBucketMetrics;
  };
  paidSharePercent: number;
  chart: {
    categories: string[];
    revenueSeries: number[];
    bookingsSeries: number[];
  };
  locations: LocationStat[];
  recentActivity: RecentActivityRow[];
  insights: DashboardInsight[];
}

const DUE_DATE_META_KEYS = [
  '_ngk_due_date',
  '_due_date',
  'due_date',
  '_payment_due_date',
  'payment_due_date',
];

function classifyLineItem(item: OrderLineItem): BookingServiceType {
  const meta = metaArrayToRecord(item.metaData);
  const productName = item.product?.node?.name || '';
  const detected = detectOrderType(meta, productName);
  if (
    detected === 'hotel' ||
    detected === 'tour' ||
    detected === 'transfer' ||
    detected === 'package'
  ) {
    return detected;
  }
  const name = productName.toLowerCase();
  if (name.includes('hotel') || meta._ngk_hotel_name || meta._ngk_hotel_booking === 'yes') {
    return 'hotel';
  }
  if (name.includes('transfer') || meta._tat_vehicle || meta._tat_pickup_name) {
    return 'transfer';
  }
  if (meta.packageInfo || name.includes('package')) {
    return 'package';
  }
  return 'tour';
}

export function parseAmount(total?: string | number | null): number {
  if (total == null || total === '') return 0;
  const rawValue =
    typeof total === 'object' ? String((total as { value?: string }).value ?? '') : String(total);
  const raw = decodeHtmlEntities(rawValue.replace(/<[^>]*>/g, ' '));
  const digitStart = raw.search(/[0-9]/);
  const focused = digitStart >= 0 ? raw.slice(digitStart) : raw;
  const cleaned = focused.replace(/,/g, '').replace(/[^0-9.-]/g, '');
  const num = Number(cleaned);
  return Number.isFinite(num) ? num : 0;
}

export function getPeriodStart(period: DashboardPeriod, now = new Date()): Date | null {
  if (period === 'ALL') return null;
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  if (period === '1M') {
    start.setMonth(start.getMonth() - 1);
    return start;
  }
  if (period === '6M') {
    start.setMonth(start.getMonth() - 6);
    return start;
  }
  start.setFullYear(start.getFullYear() - 1);
  return start;
}

export function filterByPeriod(
  orders: OrderNode[],
  period: DashboardPeriod,
  now = new Date()
): OrderNode[] {
  const start = getPeriodStart(period, now);
  if (!start) return orders;
  const startMs = start.getTime();
  return orders.filter((order) => {
    if (!order.date) return false;
    const time = new Date(order.date).getTime();
    return Number.isFinite(time) && time >= startMs && time <= now.getTime();
  });
}

export function getOrderStatusBucket(
  status?: string | null
): 'confirmed' | 'pending' | 'cancelled' | 'other' {
  const normalized = normalizeOrderStatus(status);
  if (normalized === 'COMPLETED' || normalized === 'PROCESSING' || normalized === 'CONFIRMED') {
    return 'confirmed';
  }
  if (
    normalized === 'PENDING' ||
    normalized === 'ON_HOLD' ||
    normalized === 'CANCEL_REQUEST'
  ) {
    return 'pending';
  }
  if (normalized === 'CANCELLED') {
    return 'cancelled';
  }
  return 'other';
}

export function isRevenueOrder(order: OrderNode): boolean {
  if (resolvePaymentStatus(order) !== 'Paid') return false;
  const normalized = normalizeOrderStatus(order.status);
  return (
    getOrderStatusBucket(order.status) !== 'cancelled' &&
    normalized !== 'REFUNDED' &&
    normalized !== 'FAILED'
  );
}

export function isOutstandingPayment(payment: PaymentStatusValue): boolean {
  return payment === 'Unpaid' || payment === 'Pending';
}

function parseDateValue(value?: string | null): Date | null {
  if (!value) return null;
  const raw = value.includes('T') ? value : `${value}T12:00:00`;
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function getPackageInfoDate(meta: Record<string, string>): string {
  if (!meta.packageInfo) return '';
  try {
    const pkg = JSON.parse(meta.packageInfo) as { date?: unknown };
    return typeof pkg?.date === 'string' ? pkg.date : '';
  } catch {
    return '';
  }
}

function getTravelStartDate(item: OrderLineItem, orderDate?: string): Date | null {
  const meta = metaArrayToRecord(item.metaData);
  const type = classifyLineItem(item);
  let candidate = '';
  if (type === 'hotel') candidate = meta._ngk_check_in || meta['Booking Date'] || '';
  else if (type === 'transfer') {
    candidate = meta.tat_fields_departure_date || meta._tat_departure_date || '';
  } else if (type === 'package') {
    candidate =
      getPackageInfoDate(meta) || meta['Booking Date'] || meta.booking_date || '';
  } else {
    candidate = meta['Booking Date'] || meta.booking_date || '';
  }
  return parseDateValue(candidate) || parseDateValue(orderDate);
}

/** Due date from order meta, else earliest travel/service date. */
export function getOrderDueDate(order: OrderNode): Date | null {
  const meta = metaArrayToRecord(order.metaData);
  for (const key of DUE_DATE_META_KEYS) {
    const due = parseDateValue(meta[key]);
    if (due) return due;
  }

  let earliest: Date | null = null;
  for (const item of order.lineItems?.nodes ?? []) {
    const travel = getTravelStartDate(item, order.date);
    if (!travel) continue;
    if (!earliest || travel.getTime() < earliest.getTime()) earliest = travel;
  }
  return earliest;
}

export function isOverdueInvoice(order: OrderNode, now = new Date()): boolean {
  const payment = resolvePaymentStatus(order);
  if (!isOutstandingPayment(payment)) return false;
  if (getOrderStatusBucket(order.status) === 'cancelled') return false;
  const normalized = normalizeOrderStatus(order.status);
  if (normalized === 'REFUNDED' || normalized === 'FAILED') return false;
  const due = getOrderDueDate(order);
  if (!due) return false;
  const dueEnd = new Date(due);
  dueEnd.setHours(23, 59, 59, 999);
  return dueEnd.getTime() < now.getTime();
}

function emptyBucket(): StatusBucketMetrics {
  return { count: 0, amount: 0 };
}

function primaryServiceType(order: OrderNode): BookingServiceType | 'order' {
  const items = order.lineItems?.nodes ?? [];
  if (!items.length) return 'order';
  return classifyLineItem(items[0]);
}

function serviceLabel(type: BookingServiceType | 'order'): string {
  if (type === 'hotel') return 'Hotel';
  if (type === 'tour') return 'Tour';
  if (type === 'transfer') return 'Transfer';
  if (type === 'package') return 'Package';
  return 'Order';
}

function buildSparkSeries(values: number[], points = 12): number[] {
  if (!values.length) return Array.from({ length: points }, () => 0);
  if (values.length >= points) return values.slice(-points);
  return [...Array.from({ length: points - values.length }, () => 0), ...values];
}

function monthKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

function dayKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate()
  ).padStart(2, '0')}`;
}

function formatChartLabel(key: string, granularity: 'day' | 'month'): string {
  if (granularity === 'day') {
    const d = new Date(`${key}T12:00:00`);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  }
  const [y, m] = key.split('-');
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
}

export function buildChartData(
  orders: OrderNode[],
  period: DashboardPeriod,
  now = new Date()
): { categories: string[]; revenueSeries: number[]; bookingsSeries: number[] } {
  const granularity: 'day' | 'month' = period === '1M' ? 'day' : 'month';
  const map = new Map<string, { label: string; revenue: number; bookings: number }>();

  const ensureBucket = (key: string) => {
    if (!map.has(key)) {
      map.set(key, {
        label: formatChartLabel(key, granularity),
        revenue: 0,
        bookings: 0,
      });
    }
    return map.get(key)!;
  };

  if (period === '1M') {
    for (let i = 29; i >= 0; i -= 1) {
      const d = new Date(now);
      d.setHours(12, 0, 0, 0);
      d.setDate(d.getDate() - i);
      ensureBucket(dayKey(d));
    }
  } else {
    const months = period === '6M' ? 6 : 12;
    for (let i = months - 1; i >= 0; i -= 1) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      ensureBucket(monthKey(d));
    }
  }

  orders.forEach((order) => {
    if (!order.date) return;
    const d = new Date(order.date);
    if (Number.isNaN(d.getTime())) return;
    const key = granularity === 'day' ? dayKey(d) : monthKey(d);
    const point = ensureBucket(key);
    point.bookings += 1;
    if (isRevenueOrder(order)) point.revenue += parseAmount(order.total);
  });

  const sortedKeys = Array.from(map.keys()).sort();
  return {
    categories: sortedKeys.map((key) => map.get(key)!.label),
    revenueSeries: sortedKeys.map((key) => Number(map.get(key)!.revenue.toFixed(2))),
    bookingsSeries: sortedKeys.map((key) => map.get(key)!.bookings),
  };
}

export function formatDashboardMoney(amount: number, currency?: string): string {
  return formatOrderTotal(amount.toFixed(2), currency);
}

export function buildDashboardMetrics(
  orders: OrderNode[],
  period: DashboardPeriod,
  now = new Date()
): DashboardMetrics {
  const filtered = filterByPeriod(orders, period, now);
  const currency = filtered.find((o) => o.currency)?.currency || orders[0]?.currency;

  const bookingStatus = {
    confirmed: emptyBucket(),
    pending: emptyBucket(),
    cancelled: emptyBucket(),
  };
  const orderStatus = {
    confirmed: emptyBucket(),
    pending: emptyBucket(),
    cancelled: emptyBucket(),
  };

  const serviceMap: Record<BookingServiceType, ServicePerformanceMetrics> = {
    hotel: { type: 'hotel', label: 'Hotels', count: 0, amount: 0 },
    tour: { type: 'tour', label: 'Tours', count: 0, amount: 0 },
    package: { type: 'package', label: 'Packages', count: 0, amount: 0 },
    transfer: { type: 'transfer', label: 'Transfers', count: 0, amount: 0 },
  };

  const invoices = {
    paid: { count: 0, amount: 0 } as InvoiceBucketMetrics,
    unpaid: { count: 0, amount: 0 } as InvoiceBucketMetrics,
    overdue: { count: 0, amount: 0 } as InvoiceBucketMetrics,
  };

  let totalRevenue = 0;
  let outstandingAmount = 0;
  let outstandingCount = 0;
  const locationCounts = new Map<string, number>();

  filtered.forEach((order) => {
    const amount = parseAmount(order.total);
    const bucket = getOrderStatusBucket(order.status);
    if (bucket === 'confirmed' || bucket === 'pending' || bucket === 'cancelled') {
      bookingStatus[bucket].count += 1;
      bookingStatus[bucket].amount += amount;
      orderStatus[bucket].count += 1;
      orderStatus[bucket].amount += amount;
    }

    if (isRevenueOrder(order)) totalRevenue += amount;

    const payment = resolvePaymentStatus(order);
    if (payment === 'Paid') {
      invoices.paid.count += 1;
      invoices.paid.amount += amount;
    } else if (isOutstandingPayment(payment) && bucket !== 'cancelled') {
      invoices.unpaid.count += 1;
      invoices.unpaid.amount += amount;
      outstandingAmount += amount;
      outstandingCount += 1;
      if (isOverdueInvoice(order, now)) {
        invoices.overdue.count += 1;
        invoices.overdue.amount += amount;
      }
    }

    (order.lineItems?.nodes ?? []).forEach((item) => {
      const type = classifyLineItem(item);
      const lineAmount = parseAmount(item.total) || 0;
      serviceMap[type].count += 1;
      if (isRevenueOrder(order)) {
        serviceMap[type].amount += lineAmount || amount;
      }
    });

    const country = (order.billing?.country || '').trim() || 'Unknown';
    locationCounts.set(country, (locationCounts.get(country) || 0) + 1);
  });

  const chart = buildChartData(filtered, period, now);
  const invoiceMap = buildInvoiceNumberMap(orders);

  const recentActivity: RecentActivityRow[] = [...filtered]
    .sort((a, b) => {
      const aTime = a.date ? new Date(a.date).getTime() : 0;
      const bTime = b.date ? new Date(b.date).getTime() : 0;
      return bTime - aTime;
    })
    .slice(0, 8)
    .map((order) => {
      const type = primaryServiceType(order);
      return {
        date: formatBookingDate(order.date),
        customer: getCustomerName(order),
        type: serviceLabel(type),
        reference:
          invoiceMap.get(order.databaseId) ||
          `#${order.orderNumber || order.databaseId}`,
        amount: formatOrderTotal(order.total, order.currency || currency),
        status: formatStatusLabel(order.status),
        statusKey: normalizeOrderStatus(order.status),
        orderId: order.databaseId,
      };
    });

  const maxLocation = Math.max(...Array.from(locationCounts.values()), 1);
  const locations: LocationStat[] = Array.from(locationCounts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([name, count]) => ({
      name,
      count,
      percent: Math.round((count / maxLocation) * 100),
    }));

  const paidSharePercent =
    invoices.paid.amount + invoices.unpaid.amount > 0
      ? Math.round(
          (invoices.paid.amount / (invoices.paid.amount + invoices.unpaid.amount)) * 100
        )
      : 0;

  const insights: DashboardInsight[] = [
    {
      id: 'pending',
      icon: 'calendar-clock',
      title: 'Pending Bookings',
      description: `${bookingStatus.pending.count} bookings awaiting confirmation · ${formatDashboardMoney(
        bookingStatus.pending.amount,
        currency
      )}`,
      to: '/bookings-list',
    },
    {
      id: 'unpaid',
      icon: 'file-document-outline',
      title: 'Unpaid Invoices',
      description: `${invoices.unpaid.count} unpaid · ${formatDashboardMoney(
        invoices.unpaid.amount,
        currency
      )} outstanding`,
      to: '/invoices-list',
    },
    {
      id: 'overdue',
      icon: 'alert-circle-outline',
      title: 'Overdue Invoices',
      description:
        invoices.overdue.count > 0
          ? `${invoices.overdue.count} overdue · ${formatDashboardMoney(
              invoices.overdue.amount,
              currency
            )}`
          : 'No overdue invoices for this period',
      to: '/invoices-list',
    },
  ];

  return {
    currency,
    period,
    kpis: {
      totalRevenue,
      totalBookings: filtered.length,
      totalOrders: filtered.length,
      outstandingAmount,
      outstandingCount,
      revenueSeries: buildSparkSeries(chart.revenueSeries),
      bookingsSeries: buildSparkSeries(chart.bookingsSeries),
      ordersSeries: buildSparkSeries(chart.bookingsSeries),
      outstandingSeries: buildSparkSeries(
        chart.bookingsSeries.map((count, i) =>
          count > 0 && chart.revenueSeries[i] === 0 ? count : Math.max(count - 1, 0)
        )
      ),
      bookingsConfirmed: bookingStatus.confirmed.count,
      bookingsPending: bookingStatus.pending.count,
      ordersConfirmed: orderStatus.confirmed.count,
      ordersPending: orderStatus.pending.count,
    },
    bookingStatus,
    orderStatus,
    services: [serviceMap.hotel, serviceMap.tour, serviceMap.package, serviceMap.transfer],
    invoices,
    paidSharePercent,
    chart,
    locations,
    recentActivity,
    insights,
  };
}
